import { Check, ChevronsUpDown, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import type {
  Recipe,
  RecipeFormData,
  ValidationResult,
} from './hooks/use-recipes'
import {
  type LanguageCodeData,
  MultiLanguageCodeEditor,
  type SupportedLanguage,
} from './multi-language-code-editor'

interface SubtopicOption {
  id: string
  name: string
  topicName: string
}

interface RecipeEditorModalProps {
  isOpen: boolean
  recipe?: Recipe | null // null = create mode, recipe = edit mode
  onClose: () => void
  onSave: (data: RecipeFormData) => Promise<Recipe>
  onValidate: (
    id: string,
    language: string,
    options?: {
      code?: string
      testCode?: string
      testExpectedOutput?: string
    },
  ) => Promise<ValidationResult>
  isSubmitting?: boolean
  allSubtopics?: SubtopicOption[]
}

export function RecipeEditorModal({
  isOpen,
  recipe,
  onClose,
  onSave,
  onValidate,
  isSubmitting = false,
  allSubtopics = [],
}: RecipeEditorModalProps) {
  const isEditMode = !!recipe

  // Form state
  const [name, setName] = useState('')
  const [shortDescription, setShortDescription] = useState('')
  const [descriptionDetails, setDescriptionDetails] = useState('')
  const [pseudocode, setPseudocode] = useState('')
  const [codeData, setCodeData] = useState<Record<string, LanguageCodeData>>({
    python: { definitionCode: '' },
    javascript: { definitionCode: '' },
  })
  const [selectedSubtopicIds, setSelectedSubtopicIds] = useState<string[]>([])
  const [subtopicDropdownOpen, setSubtopicDropdownOpen] = useState(false)
  const [validating, setValidating] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  // Reset form when modal opens/closes or recipe changes
  useEffect(() => {
    if (!isOpen) return

    if (recipe) {
      setName(recipe.name)
      setShortDescription(recipe.shortDescription || '')
      setDescriptionDetails(recipe.descriptionDetails || '')
      setPseudocode(recipe.pseudocode || '')
      // Convert recipe implementations array to record
      const newCodeData: Record<string, LanguageCodeData> = {
        python: { definitionCode: '' },
        javascript: { definitionCode: '' },
      }
      for (const impl of recipe.implementations) {
        newCodeData[impl.language] = {
          definitionCode: impl.code,
          testCode: impl.testCode,
          testExpectedOutput: impl.testExpectedOutput,
          lastValidatedAt: impl.lastValidatedAt,
        }
      }
      setCodeData(newCodeData)
      // Set linked subtopic IDs
      setSelectedSubtopicIds(recipe.subtopics?.map((s) => s.id) || [])
    } else {
      // Reset for create mode
      setName('')
      setShortDescription('')
      setDescriptionDetails('')
      setPseudocode('')
      setCodeData({
        python: { definitionCode: '' },
        javascript: { definitionCode: '' },
      })
      setSelectedSubtopicIds([])
    }
    setValidationError(null)
  }, [isOpen, recipe])

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  const handleValidate = async (
    language: string,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!recipe?.id) {
      // For new recipes, we need to save first before validating
      setValidationError('Please save the recipe first before validating')
      return { success: false, error: 'Save recipe first' }
    }

    setValidating(true)
    setValidationError(null)

    try {
      const langData = codeData[language]
      const result = await onValidate(recipe.id, language, {
        code: langData.definitionCode,
        testCode: langData.testCode,
        testExpectedOutput: langData.testExpectedOutput,
      })

      if (result.success) {
        // Update lastValidatedAt in local state
        setCodeData((prev) => ({
          ...prev,
          [language]: {
            ...prev[language],
            lastValidatedAt: new Date().toISOString(),
          },
        }))
        toast.success('Validation passed')
      } else {
        setValidationError(result.error || 'Validation failed')
        toast.error(result.error || 'Validation failed')
      }

      return result
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Validation failed'
      setValidationError(errorMsg)
      toast.error(errorMsg)
      return { success: false, error: errorMsg }
    } finally {
      setValidating(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast.error('Name is required')
      return
    }

    // Check if at least one language has code
    const hasCode = Object.values(codeData).some((d) => d.definitionCode.trim())
    if (!hasCode) {
      toast.error('Please add code for at least one language')
      return
    }

    // Convert codeData to array format for API
    const implementations = Object.entries(codeData)
      .filter(([, data]) => data.definitionCode.trim())
      .map(([language, data]) => ({
        language,
        code: data.definitionCode,
        testCode: data.testCode || '',
        testExpectedOutput: data.testExpectedOutput || '',
      }))

    try {
      await onSave({
        name: name.trim(),
        shortDescription: shortDescription.trim() || undefined,
        descriptionDetails: descriptionDetails.trim() || undefined,
        pseudocode: pseudocode.trim() || undefined,
        implementations,
        subtopicIds: selectedSubtopicIds,
      })
      onClose()
    } catch {
      // Error is handled by parent
    }
  }

  // Toggle subtopic selection
  const toggleSubtopic = (subtopicId: string) => {
    setSelectedSubtopicIds((prev) =>
      prev.includes(subtopicId)
        ? prev.filter((id) => id !== subtopicId)
        : [...prev, subtopicId],
    )
  }

  // Group subtopic options by topic for the dropdown (options arrive ordered
  // by topic displayOrder, so insertion order is kept).
  const groupedSubtopics = (() => {
    const groups = new Map<string, SubtopicOption[]>()
    for (const option of allSubtopics) {
      const topicName = option.topicName || 'Other'
      if (!groups.has(topicName)) {
        groups.set(topicName, [])
      }
      groups.get(topicName)!.push(option)
    }
    return Array.from(groups.entries())
  })()

  if (!isOpen) return null

  const languages: SupportedLanguage[] = ['python', 'javascript']

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            {isEditMode ? 'Edit Recipe' : 'Create Recipe'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div>
            <label className="mb-1 block text-sm font-medium text-muted-foreground">
              Name <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Binary Search"
              required
            />
          </div>

          {/* Short Description */}
          <div>
            <label className="mb-1 block text-sm font-medium text-muted-foreground">
              Short Description
            </label>
            <Input
              type="text"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              placeholder="A brief one-line summary..."
            />
          </div>

          {/* Description Details */}
          <div>
            <label className="mb-1 block text-sm font-medium text-muted-foreground">
              Description Details (Markdown)
            </label>
            <Textarea
              value={descriptionDetails}
              onChange={(e) => setDescriptionDetails(e.target.value)}
              placeholder="Detailed description with markdown formatting..."
              className="min-h-[80px]"
            />
          </div>

          {/* Pseudocode */}
          <div>
            <label className="mb-1 block text-sm font-medium text-muted-foreground">
              Pseudocode (Markdown)
            </label>
            <Textarea
              value={pseudocode}
              onChange={(e) => setPseudocode(e.target.value)}
              placeholder="Pseudocode for the algorithm..."
              className="min-h-[80px]"
            />
          </div>

          {/* Linked Subtopics */}
          {allSubtopics.length > 0 && (
            <div>
              <label className="mb-1 block text-sm font-medium text-muted-foreground">
                Linked Subtopics
              </label>
              <Popover
                open={subtopicDropdownOpen}
                onOpenChange={setSubtopicDropdownOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={subtopicDropdownOpen}
                    className="w-full justify-between"
                  >
                    {selectedSubtopicIds.length > 0
                      ? `${selectedSubtopicIds.length} subtopic${selectedSubtopicIds.length === 1 ? '' : 's'} selected`
                      : 'Select subtopics...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search subtopics..." />
                    <CommandList>
                      <CommandEmpty>No subtopics found.</CommandEmpty>
                      {groupedSubtopics.map(([topicName, options]) => (
                        <CommandGroup key={topicName} heading={topicName}>
                          {options.map((subtopic) => (
                            <CommandItem
                              key={subtopic.id}
                              value={`${topicName} ${subtopic.name}`}
                              onSelect={() => toggleSubtopic(subtopic.id)}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  selectedSubtopicIds.includes(subtopic.id)
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                              {subtopic.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {/* Show selected subtopics as badges */}
              {selectedSubtopicIds.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {selectedSubtopicIds.map((id) => {
                    const subtopic = allSubtopics.find((s) => s.id === id)
                    if (!subtopic) return null
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2 py-1 text-xs text-violet-600 dark:text-violet-400"
                      >
                        {subtopic.name}
                        <button
                          type="button"
                          onClick={() => toggleSubtopic(id)}
                          className="hover:text-violet-800 dark:hover:text-violet-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Implementations */}
          <div>
            <label className="mb-2 block text-sm font-medium text-muted-foreground">
              Implementations <span className="text-destructive">*</span>
            </label>
            <MultiLanguageCodeEditor
              value={codeData}
              onChange={setCodeData}
              languages={languages}
              showValidation={isEditMode} // Only show validation in edit mode
              onValidate={isEditMode ? handleValidate : undefined}
              validating={validating}
              height="250px"
            />
            {!isEditMode && (
              <p className="mt-2 text-xs text-muted-foreground">
                Save the recipe first, then you can add validation tests.
              </p>
            )}
          </div>

          {/* Validation Error */}
          {validationError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {validationError}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between border-t border-border pt-4">
            <div className="text-xs text-muted-foreground">
              {isEditMode
                ? 'Validation is recommended before using this recipe'
                : 'Save to enable validation testing'}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || validating}>
                {isSubmitting
                  ? 'Saving...'
                  : isEditMode
                    ? 'Update Recipe'
                    : 'Create Recipe'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
